/**
 * Contacts-collection layer — the field/item module for the `record_contacts`
 * grid, parallel to `records.ts` / `listings.ts`. Owns the item type and the
 * grid's column fields, the single layer feeding a contacts collection its
 * items + fields.
 */

import { type AugerField, type EnrichableField, getFieldType } from "../fields/fields.js"
import { type BaseItem } from "./item.js"

/** The values `record_contacts.contact_type` actually stores. Stated once here
 *  and mirrored by the `values` list on the `contact_type` column below. */
export type ContactColumnType = "email" | "phone" | "url" | "location"

/** A `record_contacts` row as the contacts grid reads it. The `record` reference
 *  column carries both the stored id (`record_id`) and its resolved entity
 *  (`record`) — the resolved one is `value` (display), the id is the raw value
 *  the editor binds. `contact_type` discriminates the leaf kind (email / phone /
 *  url / location); `value` is the contact itself. Open contract: extra joined
 *  columns may ride along per query. */
export type ContactItem = BaseItem & {
  id: number
  record_id?: number
  record?: unknown
  /** The `record_contacts.contact_type` column's own values — deliberately NOT
   *  `ContactType`, which is a union of FIELD TYPE names. Its `socials` member
   *  is a read-time filter over `url` rows by `label` (see isSocialContact),
   *  never a value this column stores, so borrowing that union and subtracting
   *  `socials` described a constraint the data model already had. */
  contact_type?: ContactColumnType
  value?: string | null
  label?: string | null
  is_primary?: boolean | null
  position?: number | null
  created_at?: string
  updated_at?: string
}

const row = (item: unknown) => item as ContactItem

/** Column specs for the contacts grid. The `record` column splits `value`
 *  (resolved entity, for display) from `rawValue` (the stored id the editor
 *  binds); scalar columns leave `rawValue` to default to `value`. */
const FIELDS: ReadonlyArray<EnrichableField> = [
  { key: "id", label: "ID", type: "number", width: 70, value: (i) => row(i).id },
  {
    key: "_record",
    label: "Record",
    type: "record",
    width: 200,
    value: (i) => row(i).record,
    rawValue: (i) => row(i).record_id,
  },
  {
    key: "contact_type",
    label: "Type",
    type: "select",
    width: 120,
    values: ["email", "phone", "url", "location"],
    value: (i) => row(i).contact_type,
  },
  {
    key: "value",
    label: "Value",
    type: "string",
    width: 260,
    isEditable: true,
    value: (i) => row(i).value || "",
  },
  {
    key: "label",
    label: "Label",
    type: "string",
    width: 160,
    isEditable: true,
    value: (i) => row(i).label || "",
  },
  {
    key: "is_primary",
    label: "Primary",
    type: "boolean",
    width: 80,
    isEditable: true,
    value: (i) => row(i).is_primary,
  },
  {
    key: "created_at",
    label: "Created",
    type: "timestamp",
    width: 180,
    value: (i) => row(i).created_at,
  },
  {
    key: "updated_at",
    label: "Updated",
    type: "timestamp",
    width: 180,
    value: (i) => row(i).updated_at,
  },
]

/** The contacts grid's enriched fields: each spec's FieldType chain resolved (so
 *  it carries that type's format/parse/operators) and `rawValue` guaranteed —
 *  defaulting to `value` for scalar columns that don't split id from entity. */
export const contactFields: AugerField[] = FIELDS.map(
  (f) =>
    ({
      ...getFieldType(f.type),
      ...f,
      rawValue: f.rawValue ?? f.value,
    }) as AugerField
)
